/**
 * Hook for Business Inbox API calls and state management.
 * Follows the pattern in useConversations.ts.
 */

import { useCallback, useEffect, useRef } from 'react'
import { useInboxStore, INBOX_PAGE_SIZE } from '@/stores/inboxStore'
import { useNavBadgeStore } from '@/stores/navBadgeStore'
import { apiClient } from '@/services/apiClient'
import { transformProposedChanges } from './useOrders'
import type { RevisionStep } from '@/types/commerce'
import { subscribeInboxEvents, type InboxEvent } from '@/services/inboxEventBus'
import type {
  InboxConversationListResponse,
  InboxConversationDetail,
  InboxEmailHeader,
  InboxMessage,
  InboxStats,
  InboxTriage,
  TriageHandlerLink,
  TriageMatch,
  TriageCustomerUpdate,
  TriageDuplicate,
  TriageTargetNeeded,
  InboxAnalytics,
  PersonaAnalytics,
  DailyActivity,
  LinkedAccount,
  LinkCodeResponse,
  NewConversationRequest,
  ConflictDetail,
  ThreadRecord,
} from '@/types/inbox'

// snake_case → camelCase conversion for inbox conversations
export function transformConversation(c: Record<string, unknown>): Record<string, unknown> {
  return {
    id: c.id,
    channel: c.channel,
    title: (c.title as string) ?? null,
    attachmentCount: (c.attachment_count as number) ?? 0,
    kind: (c.kind as string) ?? null,
    kindLabel: (c.kind_label as string) ?? null,
    flags: (c.flags as string[]) ?? [],
    decisionStatus: (c.decision_status as string) ?? null,
    lastSenderName: (c.last_sender_name as string) ?? null,
    waitingCount: typeof c.waiting_count === 'number' ? c.waiting_count : null,
    linkedOrderId: (c.linked_order_id as string) ?? null,
    linkedOrderNumber: (c.linked_order_number as string) ?? null,
    customerDisplayName: c.customer_display_name,
    customerPhone: c.customer_phone,
    personaId: c.persona_id,
    personaName: c.persona_name,
    personaAvatarUrl: c.persona_avatar_url,
    lastMessagePreview: c.last_message_preview,
    lastMessageAt: c.last_message_at,
    lastMessageSenderType: c.last_message_sender_type,
    status: c.status,
    unreadCount: c.unread_count,
    messageCount: c.message_count,
    aiAutoReplyEnabled: c.ai_auto_reply_enabled,
    previousConversationId: c.previous_conversation_id ?? null,
    resolvedReason: c.resolved_reason ?? null,
    resolvedAt: c.resolved_at ?? null,
    adAttribution: (c.ad_attribution as Record<string, unknown>) ?? null,
    bankTransferReviewRequired: (c.bank_transfer_review_required as boolean) ?? false,
    engineFault: (c.engine_fault as boolean) ?? false,
    archivedAt: c.archived_at ?? null,
    createdAt: c.created_at,
    updatedAt: c.updated_at,
  }
}

function transformMessage(m: Record<string, unknown>): InboxMessage {
  return {
    id: m.id as string,
    role: m.role as string,
    senderType: (m.sender_type as InboxMessage['senderType']) ?? null,
    content: m.content as string,
    timestamp: m.timestamp as number,
    channel: (m.channel as InboxMessage['channel']) ?? 'web',
    mediaType: (m.media_type as string) ?? null,
    mediaUrl: (m.media_url as string) ?? null,
    voiceTranscription: (m.voice_transcription as string) ?? null,
    sources: (m.sources as unknown[]) ?? null,
    channelStatus: (m.channel_status as InboxMessage['channelStatus']) ?? null,
    channelMessageId: (m.channel_message_id as string) ?? null,
    error: (m.error as string) ?? null,
    channelMetadata: (m.channel_metadata as Record<string, unknown>) ?? null,
  }
}

function transformEmailHeader(e: Record<string, unknown> | null | undefined): InboxEmailHeader | null {
  if (!e) return null
  return {
    fromAddr: e.from_addr as string,
    fromName: (e.from_name as string) ?? null,
    to: (e.to as string[]) ?? [],
    cc: (e.cc as string[]) ?? [],
    subject: (e.subject as string) ?? '',
    receivedAt: (e.received_at as string) ?? null,
  }
}

const text = (value: unknown): string | null => (typeof value === 'string' ? value : null)

/** SA-718: a handler's link is kept only when it is a path inside the app
 * (one leading slash, never `//host` or `/\host`, and no whitespace or
 * backslash anywhere: browsers strip tabs and newlines from a URL). */
export function handlerLink(value: unknown): TriageHandlerLink | null {
  if (!value || typeof value !== 'object') return null
  const { label, href } = value as Record<string, unknown>
  if (typeof label !== 'string' || !label || typeof href !== 'string') return null
  return /^\/(?![\\/])[^\s\\]*$/.test(href) ? { label, href } : null
}

/** SA-1079: the duplicate fact off the wire, or null. */
export function transformDuplicate(value: unknown): TriageDuplicate | null {
  if (!value || typeof value !== 'object') return null
  const d = value as Record<string, unknown>
  return typeof d.of_number === 'string' && typeof d.score === 'number' ? { ofNumber: d.of_number, score: d.score } : null
}

/** SA-1229: the server's per-row "decide would ask for a record" flags, or null. */
export function transformTargetNeeded(value: unknown): TriageTargetNeeded | null {
  if (!value || typeof value !== 'object') return null
  const t = value as Record<string, unknown>
  return { update: t.update === true, close: t.close === true }
}

/** SA-1044: a step off the wire; one missing its id, title or a numeric number is null. */
function transformRevisionStep(raw: unknown): RevisionStep | null {
  if (!raw || typeof raw !== 'object') return null
  const s = raw as Record<string, unknown>
  return typeof s.step === 'string' && typeof s.title === 'string' && typeof s.number === 'number'
    ? { step: s.step, number: s.number, title: s.title } : null
}

function transformRevisionSteps(raw: unknown): Record<string, RevisionStep> {
  if (!raw || typeof raw !== 'object') return {}
  const out: Record<string, RevisionStep> = {}
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    const s = transformRevisionStep(v)
    if (s) out[id] = s
  }
  return out
}

/** SA-1042: the waiting update a triage row opened, with its diff; null when none. */
function transformTriageUpdate(raw: unknown): TriageCustomerUpdate | null {
  if (!raw || typeof raw !== 'object') return null
  const u = raw as Record<string, unknown>
  const revises = u.revises as Record<string, unknown> | null | undefined
  return {
    id: u.id as string,
    kind: u.kind === 'response' ? 'response' : 'update',
    orderId: u.order_id as string,
    changes: transformProposedChanges(u.changes),
    canApply: Boolean(u.can_apply),
    applyRefusal: text(u.apply_refusal),
    // SA-1044: an older server sends no steps map: no notice rather than a wrong one.
    revises: revises && revises.steps && typeof revises.steps === 'object' ? {
      number: revises.number as number,
      steps: transformRevisionSteps(revises.steps),
      current: transformRevisionStep(revises.current),
    } : null,
  }
}

/** SA-1030: the match block off the wire, or null. A candidate missing its id or number is dropped. */
export function transformMatch(value: unknown): TriageMatch | null {
  if (!value || typeof value !== 'object') return null
  const m = value as Record<string, unknown>
  const candidates = Array.isArray(m.candidates) ? (m.candidates as Record<string, unknown>[]) : []
  return {
    extraction: (text(m.extraction) ?? 'not_run') as TriageMatch['extraction'],
    updateKind: text(m.update_kind),
    updateLabel: text(m.update_label),
    newKind: text(m.new_kind),
    newLabel: text(m.new_label),
    note: text(m.note),
    action: m.action === 'close' ? 'close' : undefined,
    candidates: candidates.flatMap((c) => {
      const recordId = text(c?.record_id)
      const recordNumber = text(c?.record_number)
      if (!recordId || !recordNumber) return []
      const changes = Array.isArray(c.changes) ? (c.changes as Record<string, unknown>[]) : []
      return [{
        recordId,
        recordNumber,
        likely: c.likely === true,
        cited: c.cited === true,
        summary: text(c.summary) ?? '',
        changes: changes.map((ch) => ({
          label: text(ch?.label) ?? '', current: text(ch?.current), proposed: text(ch?.proposed) ?? '',
        })),
        moreChanges: typeof c.more_changes === 'number' ? c.more_changes : 0,
      }]
    }),
  }
}

function transformThreadRecords(raw: unknown): ThreadRecord[] | null {
  if (!Array.isArray(raw)) return null
  return (raw as Record<string, unknown>[])
    .filter((r) => typeof r.id === 'string' && typeof r.number === 'string')
    .map((r) => ({
      id: r.id as string,
      number: r.number as string,
      state: r.state === 'closed' ? 'closed' as const : 'open' as const,
      outcome: text(r.outcome),
    }))
}

function transformTriage(t: Record<string, unknown> | null | undefined): InboxTriage | null {
  if (!t) return null
  const rows = (t.rows as Record<string, unknown>[]) ?? []
  const kinds = (t.kinds as Record<string, unknown>[]) ?? []
  return {
    mailboxId: t.mailbox_id as string,
    canReject: (t.can_reject as boolean) ?? false,
    canReinstate: (t.can_reinstate as boolean) ?? false,
    earlierDecided: typeof t.earlier_decided === 'number' ? t.earlier_decided : 0,
    kinds: kinds.map((k) => ({
      id: k.id as string, label: k.label as string, would: text(k.would) ?? '',
      canDecide: (k.can_decide as boolean) ?? false, refusal: text(k.refusal),
      takesTarget: k.takes_target === true,
      outcomes: Array.isArray(k.outcomes) && k.outcomes.length > 0
        ? (k.outcomes as unknown[]).filter((o): o is string => typeof o === 'string') : undefined,
    })),
    rows: rows.map((r) => ({
      ledgerId: r.ledger_id as string,
      isReply: (r.is_reply as boolean) ?? false,
      kind: text(r.kind),
      kindLabel: text(r.kind_label),
      proposedKind: text(r.proposed_kind),
      proposedLabel: text(r.proposed_label),
      proposedHow: text(r.proposed_how),
      decisionStatus: text(r.decision_status) as InboxTriage['rows'][number]['decisionStatus'],
      decidedHow: text(r.decided_how),
      decidedByName: text(r.decided_by_name),
      decidedAt: text(r.decided_at),
      rationale: text(r.rationale),
      handlerOutcome: text(r.handler_outcome),
      handlerRef: text(r.handler_ref),
      handlerLink: handlerLink(r.handler_link),
      errorWords: text(r.error_words),
      replyIntent: text(r.reply_intent),
      would: text(r.would),
      canDecide: (r.can_decide as boolean) ?? false,
      canRerun: (r.can_rerun as boolean) ?? false,
      providerMessageId: text(r.provider_message_id),
      canReject: typeof r.can_reject === 'boolean' ? r.can_reject : null,
      canReinstate: typeof r.can_reinstate === 'boolean' ? r.can_reinstate : null,
      recordId: text(r.record_id),
      recordNumber: text(r.record_number),
      customerUpdate: transformTriageUpdate(r.customer_update),
      match: transformMatch(r.match),
      duplicate: transformDuplicate(r.duplicate),
      targetNeeded: transformTargetNeeded(r.target_needed),
      threadRecords: transformThreadRecords(r.thread_records),
      outcome: text(r.outcome),
    })),
  }
}

export function transformDetail(d: Record<string, unknown>): InboxConversationDetail {
  return {
    id: d.id as string,
    channel: (d.channel as InboxConversationDetail['channel']) ?? 'web',
    customerDisplayName: (d.customer_display_name as string) ?? null,
    customerPhone: (d.customer_phone as string) ?? null,
    personaId: (d.persona_id as string) ?? null,
    personaName: (d.persona_name as string) ?? null,
    status: (d.status as InboxConversationDetail['status']) ?? 'active',
    aiAutoReplyEnabled: (d.ai_auto_reply_enabled as boolean) ?? true,
    messages: ((d.messages as Record<string, unknown>[]) ?? []).map(transformMessage),
    previousConversationId: (d.previous_conversation_id as string) ?? null,
    resolvedReason: (d.resolved_reason as InboxConversationDetail['resolvedReason']) ?? null,
    resolvedAt: (d.resolved_at as string) ?? null,
    adAttribution: (d.ad_attribution as InboxConversationDetail['adAttribution']) ?? null,
    archivedAt: (d.archived_at as string) ?? null,
    createdAt: d.created_at as string,
    updatedAt: d.updated_at as string,
    emailSendAuthorized: (d.email_send_authorized as boolean | null) ?? null,
    email: transformEmailHeader(d.email as Record<string, unknown> | null | undefined),
    triage: transformTriage(d.triage as Record<string, unknown> | null | undefined),
    linkedOrderId: (d.linked_order_id as string) ?? null,
    linkedOrderNumber: (d.linked_order_number as string) ?? null,
    records: transformThreadRecords(d.records) ?? [],
    missingFiles: ((d.missing_files as Record<string, unknown>[] | undefined) ?? []).map((f) => ({
      providerMessageId: String(f.provider_message_id),
      filename: String(f.filename),
      reason: String(f.reason),
    })),
  }
}

function transformStats(s: Record<string, unknown>): InboxStats {
  return {
    totalConversations: (s.total_conversations as number) ?? 0,
    activeConversations: (s.active_conversations as number) ?? 0,
    waitingConversations: (s.waiting_conversations as number) ?? 0,
    humanConversations: (s.human_conversations as number) ?? 0,
    resolvedConversations: (s.resolved_conversations as number) ?? 0,
    faultConversations: (s.fault_conversations as number) ?? 0,
    triageConversations: (s.triage_conversations as number) ?? 0,
    waitingItems: (s.waiting_items as number) ?? 0,
    totalUnread: (s.total_unread as number) ?? 0,
    conversationsToday: (s.conversations_today as number) ?? 0,
    messagesToday: (s.messages_today as number) ?? 0,
    channelBreakdown: (s.channel_breakdown as Record<string, number>) ?? {},
  }
}

function transformAnalytics(a: Record<string, unknown>): InboxAnalytics {
  const raw_breakdown = (a.persona_breakdown as Record<string, unknown>[]) ?? []
  const raw_activity = (a.activity as Record<string, unknown>[]) ?? []

  return {
    conversationsToday: (a.conversations_today as number) ?? 0,
    messagesToday: (a.messages_today as number) ?? 0,
    totalConversations: (a.total_conversations as number) ?? 0,
    totalUnread: (a.total_unread as number) ?? 0,
    activeConversations: (a.active_conversations as number) ?? 0,
    humanConversations: (a.human_conversations as number) ?? 0,
    personaBreakdown: raw_breakdown.map((p): PersonaAnalytics => ({
      personaId: (p.persona_id as string) ?? '',
      personaName: (p.persona_name as string) ?? 'Unknown',
      conversationCount: (p.conversation_count as number) ?? 0,
      messageCount: (p.message_count as number) ?? 0,
      unreadCount: (p.unread_count as number) ?? 0,
      channels: (p.channels as Record<string, number>) ?? {},
    })),
    activity: raw_activity.map((d): DailyActivity => ({
      date: (d.date as string) ?? '',
      channel: (d.channel as string) ?? 'web',
      messageCount: (d.message_count as number) ?? 0,
      conversationCount: (d.conversation_count as number) ?? 0,
    })),
    channelBreakdown: (a.channel_breakdown as Record<string, number>) ?? {},
  }
}

function transformLinkedAccount(a: Record<string, unknown>): LinkedAccount {
  return {
    id: a.id as string,
    channel: (a.channel as string) ?? 'whatsapp',
    channelUserId: (a.channel_user_id as string) ?? '',
    displayName: (a.display_name as string) ?? null,
    isVerified: (a.is_verified as boolean) ?? false,
    linkedAt: (a.linked_at as string) ?? null,
  }
}

function buildListParams(offset: number): URLSearchParams {
  const s = useInboxStore.getState()
  const params = new URLSearchParams()
  params.set('view', s.viewTab)
  if (s.channelFilter) params.set('channel', s.channelFilter)
  if (s.statusFilter) params.set('status', s.statusFilter)
  if (s.kindFilter) params.set('kind', s.kindFilter)
  if (s.flagFilter) params.set('flag', s.flagFilter)
  if (s.mailboxFilter) params.set('mailbox_id', s.mailboxFilter)
  if (s.personaFilter) params.set('persona_id', s.personaFilter)
  if (s.searchQuery) params.set('search', s.searchQuery)
  if (s.faultsView) params.set('faults', 'only')
  params.set('sort', s.sortBy)
  params.set('limit', String(INBOX_PAGE_SIZE))
  params.set('offset', String(offset))
  return params
}

export function useInbox() {
  const store = useInboxStore()
  // Count (not boolean) of in-flight fetchConversations calls: overlapping
  // calls are allowed (a tab-switch refetch must not be dropped just
  // because a prior fetch for the old view hasn't resolved yet). Each
  // call validates its own params snapshot at resolution time, so
  // concurrent resolutions can't cross-apply stale results.
  const inFlightRef = useRef(0)

  const fetchConversations = useCallback(async (
    opts: { silent?: boolean; keep?: boolean } = {},
  ) => {
    // The Triage tab reads its own queue, never this list: send nothing and
    // move nothing in the store.
    if (useInboxStore.getState().viewTab === 'triage') return
    // Snapshot the view/filter/sort params (offset pinned to 0 — this is
    // about detecting a view change, not a paging position) so a response
    // that resolves after the params changed can be discarded.
    const snapshot = buildListParams(0).toString()
    // `keep`: the pages already loaded were loaded under these same
    // parameters, so refresh quietly and merge the head over them.
    const silent = !!opts.silent
      || (!!opts.keep && useInboxStore.getState().loadedParams === snapshot)
    inFlightRef.current++

    if (!silent) store.setLoading(true)
    store.setError(null)
    try {
      const params = buildListParams(0)
      const response = await apiClient.fetch(`/api/inbox/conversations?${params}`)
      if (!response.ok) throw new Error(`Failed to fetch inbox: ${response.statusText}`)

      const data = await response.json()
      // Discard silently if the view/filters/sort changed while this
      // request was in flight — applying it (even just the total) would
      // splice stale-view rows into the current view.
      if (buildListParams(0).toString() !== snapshot) return

      const conversations = (data.conversations || []).map(transformConversation) as InboxConversationListResponse['conversations']
      // Read the stamp BEFORE this response writes it: a silent refresh
      // merges only over pages loaded under the same parameters.
      const sameParams = useInboxStore.getState().loadedParams === snapshot
      store.setLoadedParams(snapshot)
      if (silent && sameParams) {
        // SSE/poll refresh: merge page zero as the new head so a deep
        // scroll isn't truncated back to one page.
        store.mergeConversationHead(conversations, data.total || 0)
      } else {
        store.setConversationList(conversations, data.total || 0)
      }
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to load inbox')
    } finally {
      if (!silent) store.setLoading(false)
      inFlightRef.current--
    }
  }, [store])

  const fetchMore = useCallback(async () => {
    const s = useInboxStore.getState()
    if (inFlightRef.current > 0 || s.isLoadingMore) return
    if (s.conversationList.length >= s.total) return

    const snapshot = buildListParams(0).toString()
    store.setLoadingMore(true)
    store.setLoadMoreError(null)
    try {
      const params = buildListParams(s.conversationList.length)
      const response = await apiClient.fetch(`/api/inbox/conversations?${params}`)
      if (!response.ok) throw new Error(`Failed to load more: ${response.statusText}`)

      const data = await response.json()
      // Same staleness guard as fetchConversations — a view/filter switch
      // while this page was in flight means these rows (and total) belong
      // to a view that's no longer current.
      if (buildListParams(0).toString() !== snapshot) return

      const conversations = (data.conversations || []).map(transformConversation) as InboxConversationListResponse['conversations']
      store.appendConversations(conversations, data.total || 0)
    } catch (err) {
      store.setLoadMoreError(err instanceof Error ? err.message : 'Failed to load more')
    } finally {
      store.setLoadingMore(false)
    }
  }, [store])

  const fetchConversationDetail = useCallback(async (conversationId: string, { silent = false } = {}) => {
    if (!silent) store.setLoadingDetail(true)
    try {
      const response = await apiClient.fetch(`/api/inbox/conversations/${conversationId}`)
      if (!response.ok) throw new Error(`Failed to fetch conversation: ${response.statusText}`)

      const data = await response.json()
      store.setCurrentConversation(transformDetail(data))
    } catch (err) {
      if (!silent) store.setError(err instanceof Error ? err.message : 'Failed to load conversation')
    } finally {
      if (!silent) store.setLoadingDetail(false)
    }
  }, [store])

  const sendMessage = useCallback(async (conversationId: string, content: string, mediaRef?: string) => {
    try {
      const response = await apiClient.fetch(`/api/inbox/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify(mediaRef ? { content, media_ref: mediaRef } : { content }),
      })
      if (!response.ok) throw new Error('Failed to send message')

      const data = await response.json()
      const message = transformMessage(data)

      // Update the current conversation detail
      const current = useInboxStore.getState().currentConversation
      if (current && current.id === conversationId) {
        store.setCurrentConversation({
          ...current,
          messages: [...current.messages, message],
        })
      }

      return message
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to send message')
      return null
    }
  }, [store])

  const updateStatus = useCallback(async (conversationId: string, status: string) => {
    try {
      const response = await apiClient.fetch(`/api/inbox/conversations/${conversationId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      })
      if (!response.ok) throw new Error('Failed to update status')

      store.updateConversationInList(conversationId, { status: status as 'active' | 'waiting' | 'human' | 'resolved' })
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to update status')
    }
  }, [store])

  const markAsRead = useCallback(async (conversationId: string) => {
    const conv = useInboxStore.getState().conversationList.find((c) => c.id === conversationId)
    const prevUnread = conv?.unreadCount ?? 0
    // Optimistic: clear locally first so list + nav badge react instantly.
    store.updateConversationInList(conversationId, { unreadCount: 0 })
    if (prevUnread > 0) {
      store.decrementUnread(prevUnread)
      useNavBadgeStore.getState().decrementInboxUnread(1) // badge counts conversations
    }
    try {
      await apiClient.fetch(`/api/inbox/conversations/${conversationId}/read`, { method: 'PUT' })
    } catch {
      // Silent failure for read receipts — 30s poll reconciles the badge.
    }
  }, [store])

  const escalate = useCallback(async (conversationId: string) => {
    try {
      const response = await apiClient.fetch(`/api/inbox/conversations/${conversationId}/escalate`, {
        method: 'PUT',
      })
      if (!response.ok) throw new Error('Failed to escalate')
      store.updateConversationInList(conversationId, { status: 'human', aiAutoReplyEnabled: false })
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to escalate')
    }
  }, [store])

  const resumeAi = useCallback(async (conversationId: string) => {
    try {
      const response = await apiClient.fetch(`/api/inbox/conversations/${conversationId}/resume-ai`, {
        method: 'PUT',
      })
      if (!response.ok) throw new Error('Failed to resume AI')
      store.updateConversationInList(conversationId, { status: 'active', aiAutoReplyEnabled: true })
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to resume AI')
    }
  }, [store])

  const resolve = useCallback(async (conversationId: string) => {
    try {
      const response = await apiClient.fetch(`/api/inbox/conversations/${conversationId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'resolved' }),
      })
      if (!response.ok) throw new Error('Failed to resolve conversation')
      store.updateConversationInList(conversationId, { status: 'resolved' })
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to resolve conversation')
    }
  }, [store])

  const reopen = useCallback(async (conversationId: string) => {
    try {
      const response = await apiClient.fetch(`/api/inbox/conversations/${conversationId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'active' }),
      })
      if (!response.ok) throw new Error('Failed to reopen conversation')
      store.updateConversationInList(conversationId, { status: 'active' })
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to reopen conversation')
    }
  }, [store])

  const archiveConversation = useCallback(async (conversationId: string): Promise<boolean> => {
    try {
      const response = await apiClient.fetch(`/api/inbox/conversations/${conversationId}/archive`, { method: 'POST' })
      if (!response.ok) throw new Error('Failed to archive conversation')
      // The row no longer matches the open/resolved view it was archived from.
      useInboxStore.getState().removeConversationFromList(conversationId)
      return true
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to archive conversation')
      return false
    }
  }, [store])

  const unarchiveConversation = useCallback(async (conversationId: string): Promise<boolean> => {
    try {
      const response = await apiClient.fetch(`/api/inbox/conversations/${conversationId}/unarchive`, { method: 'POST' })
      if (!response.ok) throw new Error('Failed to unarchive conversation')
      // In the archived view the restored row no longer matches either.
      useInboxStore.getState().removeConversationFromList(conversationId)
      return true
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to unarchive conversation')
      return false
    }
  }, [store])

  const bulkArchive = useCallback(async (olderThanDays: number, dryRun: boolean): Promise<number | null> => {
    try {
      const response = await apiClient.fetch('/api/inbox/conversations/bulk-archive', {
        method: 'POST',
        body: JSON.stringify({ older_than_days: olderThanDays, dry_run: dryRun }),
      })
      if (!response.ok) throw new Error('Bulk archive failed')
      const data = await response.json()
      if (!dryRun) await fetchConversations()
      return (data.count as number) ?? 0
    } catch {
      return null
    }
  }, [fetchConversations])

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.fetch('/api/inbox/stats')
      if (!response.ok) throw new Error('Failed to fetch stats')
      const data = await response.json()
      store.setStats(transformStats(data))
    } catch {
      // Silent failure for stats
    }
  }, [store])

  const fetchAnalytics = useCallback(async (days: number = 7): Promise<InboxAnalytics | null> => {
    try {
      const response = await apiClient.fetch(`/api/inbox/analytics?days=${days}`)
      if (!response.ok) throw new Error('Failed to fetch analytics')
      const data = await response.json()
      return transformAnalytics(data)
    } catch {
      return null
    }
  }, [])

  // Account linking
  const generateLinkCode = useCallback(async (): Promise<LinkCodeResponse | null> => {
    try {
      const response = await apiClient.fetch('/api/inbox/link-code', { method: 'POST' })
      if (!response.ok) throw new Error('Failed to generate link code')
      const data = await response.json()
      return {
        code: data.code,
        expiresAt: data.expires_at,
        instructions: data.instructions,
      }
    } catch {
      return null
    }
  }, [])

  const fetchLinkedAccounts = useCallback(async (): Promise<LinkedAccount[]> => {
    try {
      const response = await apiClient.fetch('/api/inbox/linked-accounts')
      if (!response.ok) return []
      const data = await response.json()
      return (data as Record<string, unknown>[]).map(transformLinkedAccount)
    } catch {
      return []
    }
  }, [])

  const unlinkAccount = useCallback(async (identityId: string): Promise<boolean> => {
    try {
      const response = await apiClient.fetch(`/api/inbox/linked-accounts/${identityId}`, {
        method: 'DELETE',
      })
      return response.ok
    } catch {
      return false
    }
  }, [])

  const createConversation = useCallback(async (
    req: NewConversationRequest
  ): Promise<{ ok: true; conversation: InboxConversationDetail } | { ok: false; conflict?: ConflictDetail; error?: string }> => {
    try {
      const response = await apiClient.fetch('/api/inbox/conversations', {
        method: 'POST',
        body: JSON.stringify({
          persona_id: req.personaId,
          customer_phone: req.customerPhone,
          customer_display_name: req.customerDisplayName || null,
          initial_message: req.initialMessage,
          channel: req.channel,
          confirm_multiple_personas: req.confirmMultiplePersonas ?? false,
        }),
      })

      if (response.status === 409) {
        const detail = await response.json()
        return { ok: false, conflict: detail.detail as ConflictDetail }
      }

      if (!response.ok) {
        const body = await response.json().catch(() => null)
        const msg = body?.detail || response.statusText
        return { ok: false, error: typeof msg === 'string' ? msg : JSON.stringify(msg) }
      }

      const data = await response.json()
      const conversation = transformDetail(data)
      return { ok: true, conversation }
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Failed to create conversation' }
    }
  }, [])

  return {
    // State
    conversations: store.conversationList,
    currentConversation: store.currentConversation,
    stats: store.stats,
    total: store.total,
    isLoading: store.isLoading,
    isLoadingDetail: store.isLoadingDetail,
    error: store.error,
    channelFilter: store.channelFilter,
    statusFilter: store.statusFilter,
    kindFilter: store.kindFilter,
    flagFilter: store.flagFilter,
    mailboxFilter: store.mailboxFilter,
    searchQuery: store.searchQuery,
    sortBy: store.sortBy,
    viewTab: store.viewTab,
    faultsView: store.faultsView,
    hasMore: store.conversationList.length < store.total,
    isLoadingMore: store.isLoadingMore,
    loadMoreError: store.loadMoreError,

    // Actions
    fetchConversations,
    fetchConversationDetail,
    sendMessage,
    updateStatus,
    markAsRead,
    escalate,
    resumeAi,
    resolve,
    reopen,
    fetchStats,
    fetchAnalytics,
    generateLinkCode,
    fetchLinkedAccounts,
    unlinkAccount,
    createConversation,
    setChannelFilter: store.setChannelFilter,
    setStatusFilter: store.setStatusFilter,
    setKindFilter: store.setKindFilter,
    setFlagFilter: store.setFlagFilter,
    setMailboxFilter: store.setMailboxFilter,
    clearTriageFilters: store.clearTriageFilters,
    setSearchQuery: store.setSearchQuery,
    setSortBy: store.setSortBy,
    setCurrentConversation: store.setCurrentConversation,
    fetchMore,
    archiveConversation,
    unarchiveConversation,
    bulkArchive,
    setViewTab: store.setViewTab,
    setFaultsView: store.setFaultsView,
  }
}

/**
 * Hook for SSE connection to inbox events.
 * Call this from the InboxPage to get real-time updates.
 *
 * The actual SSE connection is owned by the module-level inboxEventBus
 * (mounted by AppShell) so it stays alive across route changes. This hook
 * is a thin adapter that subscribes to the shared bus for the lifetime of
 * the calling component.
 */
export function useInboxSSE(onEvent: (event: InboxEvent) => void) {
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  useEffect(() => subscribeInboxEvents((e) => onEventRef.current(e)), [])
}
